import React from "react"
import { isToolUIPart } from "ai"
import type { UIMessage } from "@ai-sdk/react"

import AssistantChartCard from "~components/outfitter-assistant/assistant-chart-card"
import AssistantPlanCard from "~components/outfitter-assistant/assistant-plan-card"
import AssistantUserSelectionCard from "~components/outfitter-assistant/assistant-user-selection-card"
import { getAssistantChartPresentation } from "~components/outfitter-assistant/assistant-chart-tool.helpers"
import { getAssistantPlanPresentation } from "~components/outfitter-assistant/assistant-plan-tool.helpers"
import { getUserSelectionPresentation } from "~components/outfitter-assistant/user-selection-tool.helpers"
import ChatMessageToolCallChip from "~components/shared/chat/chat-message-tool-call-chip"
import { useChat } from "~components/shared/chat/chat-provider"
import {
    buildToolCallLabel,
    getErrorText,
    getVariantFromState,
    hasAiSuggestionId,
    shouldSuppressToolCallChip,
} from "~components/shared/chat/chat-message-tool-call-helpers"

interface Props {
    readonly part: UIMessage["parts"][number]
}

export default function ChatMessageToolCall({ part }: Props) {
    const { sendUserMessage, isChatLoading } = useChat()

    if (!isToolUIPart(part)) return null
    if (hasAiSuggestionId(part)) return null

    const userSelection = getUserSelectionPresentation(part)
    const chartPresentation = getAssistantChartPresentation(part)
    const planPresentation = getAssistantPlanPresentation(part)
    const variant = getVariantFromState(part.state)
    const errorText = getErrorText(part)
    const isUserSelectionVisible = Boolean(userSelection && sendUserMessage)
    const shouldRenderChip =
        variant &&
        !shouldSuppressToolCallChip({
            part,
            isUserSelectionVisible,
            chartPresentation,
            planPresentation,
            variant,
        })

    return (
        <>
            {isUserSelectionVisible && userSelection && sendUserMessage ? (
                <AssistantUserSelectionCard
                    prompt={userSelection.prompt}
                    options={userSelection.options}
                    mode={userSelection.mode}
                    isDisabled={isChatLoading}
                    onSelect={(labels) => {
                        sendUserMessage(labels.join(", "))
                    }}
                />
            ) : null}
            {chartPresentation ? (
                <AssistantChartCard presentation={chartPresentation} />
            ) : null}
            {planPresentation ? (
                <AssistantPlanCard
                    presentation={planPresentation}
                    isDisabled={isChatLoading}
                    onContinueActiveStep={
                        sendUserMessage
                            ? (message) => sendUserMessage(message)
                            : undefined
                    }
                />
            ) : null}
            {shouldRenderChip ? (
                <ChatMessageToolCallChip
                    variant={variant}
                    label={buildToolCallLabel(part, variant)}
                    errorText={errorText}
                />
            ) : null}
        </>
    )
}
